import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@material-ui/core";
import {
  useNotify,
  useRefresh,
  useListContext,
  useDataProvider,
} from "react-admin";
import { API } from "aws-amplify";
import Header from "../utils/auth/getHeader";

export const AssignFlag = ({ open, handleClose }) => {
  const [selectedFlags, setSelectedFlags] = useState([]); // multiple flags
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { selectedIds, onUnselectItems } = useListContext();

  // Clear the selected flags each time the dialog is opened
  useEffect(() => {
    if (open) {
      setSelectedFlags([]);
    }
  }, [open]);

  // Load flags from the dataProvider
  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("flag", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "resource_name" },
        filter: {},
      })
      .then(({ data }) => {
        setFlags(data);
      })
      .catch((error) => {
        notify("Error loading flags: " + error.message, { type: "warning" });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataProvider, notify]);

  // Pre-select the user's existing flags when the modal opens
  useEffect(() => {
    if (open && selectedIds && selectedIds.length === 1) {
      setLoading(true);
      const userId = selectedIds[0].split("+")[0] + "+profile";
      dataProvider
        .getOne("user", { id: userId })
        .then(({ data }) => {
          setSelectedFlags(Array.isArray(data.flags) ? data.flags : []);
        })
        .catch(() => {
          setSelectedFlags([]);
        })
        .finally(() => setLoading(false));
    } else if (open) {
      setSelectedFlags([]);
    }
  }, [open, selectedIds, dataProvider]);

  const handleAssign = async () => {
    const isMultiple = selectedIds && selectedIds.length > 1;
    try {
      const header = await Header.getHeader();
      await API.post("secure-api", "api/admin/flagassign", {
        headers: {
          ...header,
          "Access-Control-Allow-Headers": "*",
          "Access-Control-Allow-Origin": "*",
        },
        body: {
          user_ids: selectedIds,
          resource_ids: selectedFlags,
          multiple: isMultiple
        },
      });
      notify("Users updated successfully", { type: "info" });
      onUnselectItems();
      refresh();
      handleClose();
    } catch (error) {
      notify("Error: Users not updated", { type: "warning" });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Assign Flag</DialogTitle>
      <DialogContent>
        {loading ? (
          <p>Loading flags...</p>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="flag-select-label">Flags</InputLabel>
            <Select
              labelId="flag-select-label"
              id="flag-select"
              multiple
              value={
                flags
                  .filter((flag) =>
                    selectedFlags.some(
                      (sel) => String(sel).split("+")[0] === String(flag.id).split("+")[0]
                    )
                  )
                  .map((flag) => flag.id)
              }
              onChange={(e) => {
                setSelectedFlags(e.target.value);
              }}
              renderValue={(selected) =>
                flags
                  .filter((flag) => selected.includes(flag.id))
                  .map((f) => f.resource_name)
                  .join(", ")
              }
            >
              {flags.map((flagItem) => (
                <MenuItem key={flagItem.id} value={flagItem.id}>
                  <Checkbox
                    checked={selectedFlags.some(
                      (sel) => String(sel).split("+")[0] === String(flagItem.id).split("+")[0]
                    )}
                  />
                  <ListItemText primary={flagItem.resource_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleAssign} color="primary" disabled={loading}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};
